import React, {
    useEffect,
    useRef,
    useState
} from "react";

import AdminPagination from "./AdminPagination";

import api, {
    BACKEND_URL,
    showAlert
} from "../../util/api";


const Media = () => {

    /*
    |--------------------------------------------------------------------------
    | Existing State
    |--------------------------------------------------------------------------
    */

    const [media, setMedia] = useState([]);

    const [loading, setLoading] =
        useState(true);

    const [uploading, setUploading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [pagination, setPagination] =
        useState(null);

    const [page, setPage] =
        useState(1);

    const [perPage, setPerPage] =
        useState(24);

    const [search, setSearch] =
        useState("");

    const [folder, setFolder] =
        useState("");


    /*
    |--------------------------------------------------------------------------
    | Modals
    |--------------------------------------------------------------------------
    */

    const [showUploadModal, setShowUploadModal] =
        useState(false);

    const [showEditModal, setShowEditModal] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | Selected Media
    |--------------------------------------------------------------------------
    */

    const [selectedMedia, setSelectedMedia] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | Upload State
    |--------------------------------------------------------------------------
    */

    const [uploadForm, setUploadForm] =
        useState({
            files: [],
            alt_text: "",
            caption: "",
            folder: "general",
        });


    /*
    |--------------------------------------------------------------------------
    | Upload Progress
    |--------------------------------------------------------------------------
    */

    const [uploadItems, setUploadItems] =
        useState([]);


    /*
    |--------------------------------------------------------------------------
    | File Input
    |--------------------------------------------------------------------------
    */

    const fileInputRef =
        useRef(null);


    /*
    |--------------------------------------------------------------------------
    | Edit Form
    |--------------------------------------------------------------------------
    */

    const [editForm, setEditForm] =
        useState({
            name: "",
            alt_text: "",
            caption: "",
            folder: "general",
        });


    /*
    |--------------------------------------------------------------------------
    | Fetch Media
    |--------------------------------------------------------------------------
    */

    const fetchMedia = async (
        requestedPage = page
    ) => {

        try {

            setLoading(true);

            const params =
                new URLSearchParams();

            params.set(
                "page",
                requestedPage
            );

            params.set(
                "per_page",
                perPage
            );


            if (search.trim()) {

                params.set(
                    "search",
                    search.trim()
                );

            }


            if (folder) {

                params.set(
                    "folder",
                    folder
                );

            }


            const response =
                await api.get(
                    `/admin/media?${params.toString()}`
                );


            const result =
                await response.data;


            if (result.success) {

                setMedia(
                    result.data.data
                );

                setPagination(
                    result.data
                );

            }

        } catch (error) {

            console.error(
                "Error fetching media:",
                error
            );

        } finally {

            setLoading(false);

        }
    };


    useEffect(() => {

        setPage(1);

        fetchMedia(1);

    }, [
        search,
        folder,
        perPage
    ]);


    /*
    |--------------------------------------------------------------------------
    | Upload Form
    |--------------------------------------------------------------------------
    */

    const resetUploadForm = () => {

        setUploadForm({
            files: [],
            alt_text: "",
            caption: "",
            folder: "general",
        });

        setUploadItems([]);

        if (fileInputRef.current) {

            fileInputRef.current.value =
                "";

        }
    };


    const openUploadModal = () => {

        resetUploadForm();

        setShowUploadModal(true);

    };


    /*
    |--------------------------------------------------------------------------
    | File Selection
    |--------------------------------------------------------------------------
    */

    const handleUploadChange = (e) => {

        const {
            name,
            value,
            files
        } = e.target;


        if (name === "file") {

            const selectedFiles =
                Array.from(files || []);


            setUploadForm(
                previous => ({
                    ...previous,

                    files:
                        selectedFiles
                })
            );


            setUploadItems(
                selectedFiles.map(
                    (file, index) => ({
                        id:
                            `${Date.now()}-${index}-${file.name}`,

                        file,

                        progress: 0,

                        status:
                            "pending",

                        message:
                            ""
                    })
                )
            );

            return;
        }


        setUploadForm(
            previous => ({
                ...previous,

                [name]: value
            })
        );
    };


    /*
    |--------------------------------------------------------------------------
    | Upload Helpers
    |--------------------------------------------------------------------------
    */

    const updateUploadItem = (
        id,
        changes
    ) => {

        setUploadItems(
            previous =>
                previous.map(item =>
                    item.id === id
                        ? {
                            ...item,
                            ...changes
                        }
                        : item
                )
        );
    };


    const getUploadItem = (id) => {

        return uploadItems.find(
            item => item.id === id
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Upload One File
    |--------------------------------------------------------------------------
    */

    const uploadSingleFile = async (
        uploadItem
    ) => {

        const {
            id,
            file
        } = uploadItem;


        updateUploadItem(
            id,
            {
                status: "uploading",
                progress: 0,
                message: "Uploading..."
            }
        );


        const formData =
            new FormData();


        formData.append(
            "file",
            file
        );


        formData.append(
            "alt_text",
            uploadForm.alt_text
        );


        formData.append(
            "caption",
            uploadForm.caption
        );


        formData.append(
            "folder",
            uploadForm.folder
        );


        try {

            const response =
                await api.post(
                    "/admin/media",
                    formData,
                    {

                        headers: {
                            "Content-Type":
                                "multipart/form-data"
                        },

                        onUploadProgress:
                            (progressEvent) => {

                                const total =
                                    progressEvent.total;

                                if (!total) {
                                    return;
                                }


                                const progress =
                                    Math.round(
                                        (
                                            progressEvent.loaded /
                                            total
                                        ) * 100
                                    );


                                updateUploadItem(
                                    id,
                                    {
                                        progress
                                    }
                                );

                            }

                    }
                );


            const result =
                await response.data;


            if (
                result.success === false
            ) {

                updateUploadItem(
                    id,
                    {
                        status: "error",

                        message:
                            result.message ||
                            "Upload failed.",

                        progress: 0
                    }
                );

                return false;
            }


            updateUploadItem(
                id,
                {
                    status: "success",

                    message:
                        "Uploaded successfully.",

                    progress: 100
                }
            );


            return true;


        } catch (error) {

            console.error(
                "Upload error:",
                error
            );


            let message =
                "Upload failed.";


            if (
                error?.response?.data?.message
            ) {

                message =
                    error.response.data.message;

            }


            if (
                error?.response?.data?.errors
            ) {

                const errors =
                    error.response.data.errors;


                const firstKey =
                    Object.keys(errors)[0];


                if (
                    firstKey &&
                    errors[firstKey]?.length
                ) {

                    message =
                        errors[firstKey][0];

                }

            }


            updateUploadItem(
                id,
                {
                    status: "error",

                    message,

                    progress: 0
                }
            );


            return false;
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Upload Multiple Files
    |--------------------------------------------------------------------------
    */

    const handleUpload = async (e) => {

        e.preventDefault();


        if (
            !uploadForm.files ||
            uploadForm.files.length === 0
        ) {

            showAlert(
                "error",
                "Error!",
                "Please select at least one file."
            );

            return;
        }


        try {

            setUploading(true);


            let successCount = 0;


            /*
             * Upload files sequentially.
             *
             * This prevents the browser/server from being
             * overloaded when the user selects many large files.
             */
            for (
                const uploadItem
                of uploadItems
            ) {

                /*
                 * Don't upload already successful items.
                 */
                if (
                    uploadItem.status ===
                    "success"
                ) {
                    successCount++;

                    continue;
                }


                const success =
                    await uploadSingleFile(
                        uploadItem
                    );


                if (success) {

                    successCount++;

                }

            }


            /*
             * Refresh media library.
             */
            await fetchMedia(page);


            /*
             * Show result.
             */
            const total =
                uploadItems.length;


            if (
                successCount === total
            ) {

                showAlert(
                    "success",
                    "Success!",
                    `${successCount} file${successCount !== 1 ? "s" : ""} uploaded successfully.`
                );


                /*
                 * Close after successful uploads.
                 */
                setTimeout(() => {

                    setShowUploadModal(
                        false
                    );

                    resetUploadForm();

                }, 500);


            } else if (
                successCount > 0
            ) {

                showAlert(
                    "warning",
                    "Partially Complete",
                    `${successCount} of ${total} files uploaded successfully.`
                );


            } else {

                showAlert(
                    "error",
                    "Upload Failed!",
                    "None of the selected files could be uploaded."
                );

            }

        } catch (error) {

            console.error(
                "Upload error:",
                error
            );

            showAlert(
                "error",
                "Error!",
                "Something went wrong while uploading."
            );

        } finally {

            setUploading(false);

        }
    };


    /*
    |--------------------------------------------------------------------------
    | Edit
    |--------------------------------------------------------------------------
    */

    const openEditModal = (item) => {

        setSelectedMedia(item);


        setEditForm({
            name:
                item.name || "",

            alt_text:
                item.alt_text || "",

            caption:
                item.caption || "",

            folder:
                item.folder || "general",
        });


        setShowEditModal(true);

    };


    const handleEditChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setEditForm(
            previous => ({
                ...previous,

                [name]: value
            })
        );

    };


    const handleUpdate = async (e) => {

        e.preventDefault();


        if (!selectedMedia) {
            return;
        }


        try {

            setSaving(true);


            const response =
                await api.post(
                    `/admin/media/${selectedMedia.id}`,
                    editForm,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data"
                        }
                    }
                );


            const result =
                await response.data;


            if (
                result.success === false
            ) {

                showAlert(
                    "error",
                    "Error!",
                    result.message ||
                    "Unable to update media."
                );

                return;
            }


            showAlert(
                "success",
                "Success!",
                "Media updated successfully."
            );


            setShowEditModal(false);

            setSelectedMedia(null);

            fetchMedia(page);


        } catch (error) {

            console.error(error);


            showAlert(
                "error",
                "Error!",
                "Something went wrong while updating media."
            );

        } finally {

            setSaving(false);

        }
    };


    /*
    |--------------------------------------------------------------------------
    | Delete
    |--------------------------------------------------------------------------
    */

    const handleDelete = async (
        item
    ) => {

        const confirmed =
            window.confirm(
                `Delete "${item.name}"? This will permanently remove the file.`
            );


        if (!confirmed) {
            return;
        }


        try {

            const response =
                await api.delete(
                    `/admin/media/${item.id}`
                );


            const result =
                await response.data;


            if (result.success) {

                showAlert(
                    "success",
                    "Success!",
                    "Media deleted successfully."
                );


                fetchMedia(
                    media.length === 1 &&
                    page > 1
                        ? page - 1
                        : page
                );


            } else {

                showAlert(
                    "error",
                    "Error!",
                    result.message ||
                    "Unable to delete media."
                );

            }

        } catch (error) {

            console.error(error);

            showAlert(
                "error",
                "Error!",
                "Something went wrong while deleting media."
            );

        }
    };


    /*
    |--------------------------------------------------------------------------
    | Helpers
    |--------------------------------------------------------------------------
    */

    const getFileUrl = (path) => {

        if (!path) {
            return "";
        }


        if (path.startsWith("http")) {
            return path;
        }


        return (
            `${BACKEND_URL}/storage/${path}`
        );

    };


    const formatFileSize = (
        bytes
    ) => {

        if (!bytes) {
            return "—";
        }


        if (bytes < 1024) {

            return `${bytes} B`;

        }


        if (
            bytes <
            1024 * 1024
        ) {

            return `${(
                bytes / 1024
            ).toFixed(1)} KB`;

        }


        if (
            bytes <
            1024 * 1024 * 1024
        ) {

            return `${(
                bytes /
                (1024 * 1024)
            ).toFixed(1)} MB`;

        }


        return `${(
            bytes /
            (1024 * 1024 * 1024)
        ).toFixed(1)} GB`;

    };


    const getMediaType = (
        item
    ) => {

        if (item.media_type) {
            return item.media_type;
        }


        if (
            item.mime_type?.startsWith(
                "image/"
            )
        ) {
            return "image";
        }


        if (
            item.mime_type?.startsWith(
                "video/"
            )
        ) {
            return "video";
        }


        return "document";

    };


    const isImage = (item) => {

        return (
            getMediaType(item) ===
            "image"
        );

    };


    const isVideo = (item) => {

        return (
            getMediaType(item) ===
            "video"
        );

    };


    const isDocument = (item) => {

        return (
            getMediaType(item) ===
            "document"
        );

    };


    const getDocumentIcon = (
        item
    ) => {

        const mime =
            item.mime_type || "";


        if (
            mime.includes("pdf")
        ) {
            return "PDF";
        }


        if (
            mime.includes("word") ||
            mime.includes("document")
        ) {
            return "DOC";
        }


        if (
            mime.includes("excel") ||
            mime.includes("spreadsheet") ||
            mime.includes("csv")
        ) {
            return "XLS";
        }


        if (
            mime.includes("powerpoint") ||
            mime.includes("presentation")
        ) {
            return "PPT";
        }


        if (
            mime.includes("zip")
        ) {
            return "ZIP";
        }


        return "FILE";
    };


    const getUploadStatusText = (
        item
    ) => {

        if (
            item.status ===
            "pending"
        ) {
            return "Waiting...";
        }


        if (
            item.status ===
            "uploading"
        ) {
            return `Uploading ${item.progress}%`;
        }


        if (
            item.status ===
            "success"
        ) {
            return "Uploaded successfully";
        }


        return item.message ||
            "Upload failed";
    };


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="admin-page">

            {/* HEADER */}

            <div className="admin-page-header">

                <div>

                    <div className="admin-eyebrow">
                        CONTENT MANAGEMENT
                    </div>


                    <h1>
                        Media Library
                    </h1>


                    <p>
                        Manage photographs,
                        videos, documents,
                        banners and other
                        assets used across
                        the website.
                    </p>

                </div>


                <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={
                        openUploadModal
                    }
                >
                    + Upload Media
                </button>

            </div>


            {/* FILTERS */}

            <div className="admin-card media-filter-card">

                <div>

                    <label>
                        Search
                    </label>


                    <input
                        type="text"
                        value={search}
                        onChange={(e) =>
                            setSearch(
                                e.target.value
                            )
                        }
                        placeholder="Search media..."
                    />

                </div>


                <div>

                    <label>
                        Folder
                    </label>


                    <select
                        value={folder}
                        onChange={(e) =>
                            setFolder(
                                e.target.value
                            )
                        }
                    >

                        <option value="">
                            All Folders
                        </option>


                        <option value="general">
                            General
                        </option>


                        <option value="news">
                            News
                        </option>


                        <option value="projects">
                            Projects
                        </option>


                        <option value="gallery">
                            Gallery
                        </option>


                        <option value="profile">
                            Profile
                        </option>

                    </select>

                </div>


                <div>

                    <label>
                        Per Page
                    </label>


                    <select
                        value={perPage}
                        onChange={(e) =>
                            setPerPage(
                                Number(
                                    e.target.value
                                )
                            )
                        }
                    >

                        <option value="12">
                            12
                        </option>

                        <option value="24">
                            24
                        </option>

                        <option value="48">
                            48
                        </option>

                    </select>

                </div>

            </div>


            {/* MEDIA */}

            <div className="admin-card">

                <div className="admin-card-header">

                    <div>

                        <h2>
                            Library
                        </h2>


                        <span>
                            {pagination?.total ||
                                0}{" "}
                            media files
                        </span>

                    </div>

                </div>


                <div className="media-library">

                    {loading ? (

                        <div className="admin-loading">
                            Loading media...
                        </div>

                    ) : media.length === 0 ? (

                        <div className="admin-empty">

                            <div className="admin-empty-icon">
                                ◫
                            </div>


                            <h3>
                                Media library is empty
                            </h3>


                            <p>
                                Upload images,
                                videos or documents
                                to start building
                                your media library.
                            </p>


                            <button
                                type="button"
                                className="admin-btn admin-btn-primary"
                                onClick={
                                    openUploadModal
                                }
                            >
                                Upload Media
                            </button>

                        </div>

                    ) : (

                        <div className="media-grid">

                            {media.map(
                                (item) => (

                                    <div
                                        className="media-item"
                                        key={item.id}
                                    >

                                        <div className="media-preview">

                                            {isImage(
                                                item
                                            ) ? (

                                                <img
                                                    src={getFileUrl(
                                                        item.file_path
                                                    )}
                                                    alt={
                                                        item.alt_text ||
                                                        item.name
                                                    }
                                                />

                                            ) : isVideo(
                                                item
                                            ) ? (

                                                <video
                                                    src={getFileUrl(
                                                        item.file_path
                                                    )}
                                                    controls
                                                    preload="metadata"
                                                />

                                            ) : (

                                                <a
                                                    href={getFileUrl(
                                                        item.file_path
                                                    )}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="media-document-preview"
                                                >

                                                    <div className="document-icon">
                                                        {getDocumentIcon(
                                                            item
                                                        )}
                                                    </div>


                                                    <span>
                                                        Open document
                                                    </span>

                                                </a>

                                            )}


                                            <div className="media-overlay">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        openEditModal(
                                                            item
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </button>


                                                <button
                                                    type="button"
                                                    className="danger"
                                                    onClick={() =>
                                                        handleDelete(
                                                            item
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </button>

                                            </div>

                                        </div>


                                        <div className="media-info">

                                            <strong
                                                title={
                                                    item.name
                                                }
                                            >
                                                {item.name}
                                            </strong>


                                            <div>
                                                {isImage(
                                                    item
                                                ) &&
                                                item.width &&
                                                item.height
                                                    ? `${item.width} × ${item.height}`
                                                    : isVideo(
                                                        item
                                                    )
                                                        ? "Video"
                                                        : "Document"}
                                            </div>


                                            <div>
                                                {formatFileSize(
                                                    item.file_size
                                                )}
                                            </div>


                                            <span>
                                                {item.folder ||
                                                    "general"}
                                            </span>

                                        </div>

                                    </div>

                                )
                            )}

                        </div>

                    )}

                </div>


                {pagination && (

                    <AdminPagination
                        pagination={
                            pagination
                        }
                        onPageChange={(
                            newPage
                        ) => {

                            setPage(
                                newPage
                            );

                            fetchMedia(
                                newPage
                            );

                        }}
                    />

                )}

            </div>


            {/* ========================================================= */}
            {/* UPLOAD MODAL */}
            {/* ========================================================= */}

            {showUploadModal && (

                <div
                    className="admin-modal-overlay"
                    onMouseDown={(e) => {

                        if (
                            e.target ===
                            e.currentTarget &&
                            !uploading
                        ) {

                            setShowUploadModal(
                                false
                            );

                        }

                    }}
                >

                    <div className="admin-modal">

                        <div className="admin-modal-header">

                            <div>

                                <div className="admin-eyebrow">
                                    MEDIA LIBRARY
                                </div>


                                <h2>
                                    Upload Media
                                </h2>

                            </div>


                            <button
                                type="button"
                                className="admin-modal-close"
                                disabled={
                                    uploading
                                }
                                onClick={() =>
                                    setShowUploadModal(
                                        false
                                    )
                                }
                            >
                                ×
                            </button>

                        </div>


                        <form
                            className="admin-form"
                            onSubmit={
                                handleUpload
                            }
                        >

                            {/* FILE INPUT */}

                            <div className="admin-form-group">

                                <label>
                                    Files *
                                </label>


                                <input
                                    ref={
                                        fileInputRef
                                    }
                                    type="file"
                                    name="file"
                                    multiple
                                    accept="
                                        image/jpeg,
                                        image/png,
                                        image/webp,
                                        image/gif,
                                        video/mp4,
                                        video/mpeg,
                                        video/quicktime,
                                        video/webm,
                                        video/ogg,
                                        application/pdf,
                                        application/msword,
                                        application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                        application/vnd.ms-excel,
                                        application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                                        application/vnd.ms-powerpoint,
                                        application/vnd.openxmlformats-officedocument.presentationml.presentation,
                                        text/plain,
                                        text/csv,
                                        application/rtf,
                                        application/zip
                                    "
                                    onChange={
                                        handleUploadChange
                                    }
                                    disabled={
                                        uploading
                                    }
                                    required
                                />


                                <small>
                                    You can select multiple
                                    images, videos or
                                    documents at once.
                                    Maximum 100 MB per file.
                                </small>

                            </div>


                            {/* SELECTED FILES */}

                            {uploadItems.length >
                                0 && (

                                    <div className="upload-list">

                                        <div className="upload-list-header">

                                            <strong>
                                                Selected Files
                                            </strong>


                                            <span>
                                                {
                                                    uploadItems.length
                                                }{" "}
                                                file
                                                {uploadItems.length !==
                                                1
                                                    ? "s"
                                                    : ""}
                                            </span>

                                        </div>


                                        {uploadItems.map(
                                            (item) => (

                                                <div
                                                    className="upload-item"
                                                    key={
                                                        item.id
                                                    }
                                                >

                                                    <div className="upload-item-top">

                                                        <div className="upload-file-icon">

                                                            {item.file.type?.startsWith(
                                                                "image/"
                                                            )
                                                                ? "IMG"
                                                                : item.file.type?.startsWith(
                                                                    "video/"
                                                                )
                                                                    ? "VID"
                                                                    : "DOC"}

                                                        </div>


                                                        <div className="upload-file-details">

                                                            <strong
                                                                title={
                                                                    item.file.name
                                                                }
                                                            >
                                                                {
                                                                    item.file.name
                                                                }
                                                            </strong>


                                                            <span>
                                                                {
                                                                    formatFileSize(
                                                                        item.file.size
                                                                    )
                                                                }
                                                            </span>

                                                        </div>


                                                        <div
                                                            className={`upload-status upload-status-${item.status}`}
                                                        >
                                                            {
                                                                getUploadStatusText(
                                                                    item
                                                                )
                                                            }
                                                        </div>

                                                    </div>


                                                    <div className="upload-progress">

                                                        <div
                                                            className="upload-progress-bar"
                                                            style={{
                                                                width: `${item.progress}%`
                                                            }}
                                                        />

                                                    </div>


                                                    <div className="upload-progress-info">

                                                        <span>
                                                            {item.status ===
                                                            "success"
                                                                ? "Complete"
                                                                : item.status ===
                                                                    "error"
                                                                    ? item.message
                                                                    : `${item.progress}%`}
                                                        </span>


                                                        {item.status ===
                                                            "success" && (
                                                                <span>
                                                                    ✓
                                                                </span>
                                                            )}

                                                    </div>

                                                </div>

                                            )
                                        )}

                                    </div>

                                )}


                            {/* FOLDER */}

                            <div className="admin-form-group">

                                <label>
                                    Folder
                                </label>


                                <select
                                    name="folder"
                                    value={
                                        uploadForm.folder
                                    }
                                    onChange={
                                        handleUploadChange
                                    }
                                    disabled={
                                        uploading
                                    }
                                >

                                    <option value="general">
                                        General
                                    </option>


                                    <option value="news">
                                        News
                                    </option>


                                    <option value="projects">
                                        Projects
                                    </option>


                                    <option value="gallery">
                                        Gallery
                                    </option>


                                    <option value="profile">
                                        Profile
                                    </option>

                                </select>

                            </div>


                            {/* ALT TEXT */}

                            <div className="admin-form-group">

                                <label>
                                    Alt Text
                                </label>


                                <input
                                    type="text"
                                    name="alt_text"
                                    value={
                                        uploadForm.alt_text
                                    }
                                    onChange={
                                        handleUploadChange
                                    }
                                    placeholder="Describe the image"
                                    disabled={
                                        uploading
                                    }
                                />

                            </div>


                            {/* CAPTION */}

                            <div className="admin-form-group">

                                <label>
                                    Caption
                                </label>


                                <textarea
                                    name="caption"
                                    rows="3"
                                    value={
                                        uploadForm.caption
                                    }
                                    onChange={
                                        handleUploadChange
                                    }
                                    placeholder="Optional media caption"
                                    disabled={
                                        uploading
                                    }
                                />

                            </div>


                            {/* FOOTER */}

                            <div className="admin-modal-footer">

                                <button
                                    type="button"
                                    className="admin-btn admin-btn-secondary"
                                    disabled={
                                        uploading
                                    }
                                    onClick={() =>
                                        setShowUploadModal(
                                            false
                                        )
                                    }
                                >
                                    Cancel
                                </button>


                                <button
                                    type="submit"
                                    className="admin-btn admin-btn-primary"
                                    disabled={
                                        uploading ||
                                        uploadItems.length ===
                                        0
                                    }
                                >

                                    {uploading
                                        ? "Uploading..."
                                        : uploadItems.length >
                                            0
                                            ? `Upload ${uploadItems.length} File${uploadItems.length !== 1 ? "s" : ""}`
                                            : "Upload Media"}

                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}


            {/* ========================================================= */}
            {/* EDIT MODAL */}
            {/* ========================================================= */}

            {showEditModal &&
                selectedMedia && (

                    <div
                        className="admin-modal-overlay"
                        onMouseDown={(e) => {

                            if (
                                e.target ===
                                e.currentTarget
                            ) {

                                setShowEditModal(
                                    false
                                );

                            }

                        }}
                    >

                        <div className="admin-modal">

                            <div className="admin-modal-header">

                                <div>

                                    <div className="admin-eyebrow">
                                        MEDIA
                                    </div>


                                    <h2>
                                        Edit Media
                                    </h2>

                                </div>


                                <button
                                    type="button"
                                    className="admin-modal-close"
                                    onClick={() =>
                                        setShowEditModal(
                                            false
                                        )
                                    }
                                >
                                    ×
                                </button>

                            </div>


                            {/* PREVIEW */}

                            <div className="media-edit-preview">

                                {isImage(
                                    selectedMedia
                                ) ? (

                                    <img
                                        src={getFileUrl(
                                            selectedMedia.file_path
                                        )}
                                        alt=""
                                    />

                                ) : isVideo(
                                    selectedMedia
                                ) ? (

                                    <video
                                        src={getFileUrl(
                                            selectedMedia.file_path
                                        )}
                                        controls
                                    />

                                ) : (

                                    <a
                                        href={getFileUrl(
                                            selectedMedia.file_path
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="media-document-preview"
                                    >

                                        <div className="document-icon">
                                            {getDocumentIcon(
                                                selectedMedia
                                            )}
                                        </div>


                                        <span>
                                            Open Document
                                        </span>

                                    </a>

                                )}

                            </div>


                            <form
                                className="admin-form"
                                onSubmit={
                                    handleUpdate
                                }
                            >

                                <div className="admin-form-group">

                                    <label>
                                        Name
                                    </label>


                                    <input
                                        type="text"
                                        name="name"
                                        value={
                                            editForm.name
                                        }
                                        onChange={
                                            handleEditChange
                                        }
                                    />

                                </div>


                                <div className="admin-form-group">

                                    <label>
                                        Folder
                                    </label>


                                    <select
                                        name="folder"
                                        value={
                                            editForm.folder
                                        }
                                        onChange={
                                            handleEditChange
                                        }
                                    >

                                        <option value="general">
                                            General
                                        </option>


                                        <option value="news">
                                            News
                                        </option>


                                        <option value="projects">
                                            Projects
                                        </option>


                                        <option value="gallery">
                                            Gallery
                                        </option>


                                        <option value="profile">
                                            Profile
                                        </option>

                                    </select>

                                </div>


                                <div className="admin-form-group">

                                    <label>
                                        Alt Text
                                    </label>


                                    <input
                                        type="text"
                                        name="alt_text"
                                        value={
                                            editForm.alt_text
                                        }
                                        onChange={
                                            handleEditChange
                                        }
                                    />

                                </div>


                                <div className="admin-form-group">

                                    <label>
                                        Caption
                                    </label>


                                    <textarea
                                        name="caption"
                                        rows="3"
                                        value={
                                            editForm.caption
                                        }
                                        onChange={
                                            handleEditChange
                                        }
                                    />

                                </div>


                                <div className="admin-modal-footer">

                                    <button
                                        type="button"
                                        className="admin-btn admin-btn-secondary"
                                        onClick={() =>
                                            setShowEditModal(
                                                false
                                            )
                                        }
                                    >
                                        Cancel
                                    </button>


                                    <button
                                        type="submit"
                                        className="admin-btn admin-btn-primary"
                                        disabled={
                                            saving
                                        }
                                    >

                                        {saving
                                            ? "Saving..."
                                            : "Save Changes"}

                                    </button>

                                </div>

                            </form>

                        </div>

                    </div>

                )}

        </div>
    );
};


export default Media;